import { getApiBaseUrl } from '../config/apiConfig';

const API_BASE_URL = getApiBaseUrl();

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token') || localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// Helper function to handle fetch responses
const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('token');
      localStorage.removeItem('user_data');
    }
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || `HTTP error! status: ${response.status}`);
  }
  return await response.json();
};

const hebergementService = {
  // Récupérer les statistiques des hébergements
  getStats: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/stats`, {
        headers: getAuthHeaders()
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching hebergement stats:', error);
      throw error;
    }
  },

  // Récupérer la liste des sites avec pagination et filtres
  getSites: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await fetch(`${API_BASE_URL}/hebergement/list?${queryParams}`, {
        headers: getAuthHeaders()
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching sites list:', error);
      throw error;
    }
  },

  // Récupérer un site par ID
  getSiteById: async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/${id}`, {
        headers: getAuthHeaders()
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching site by ID:', error);
      throw error;
    }
  },

  updateSectionActivite: async (siteId, sectionActivite) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/${siteId}/section-activite`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ sectionActivite }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating section activite:', error);
      throw error;
    }
  },

  updateTypeSite: async (siteId, typeSite) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/${siteId}/type-site`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ typeSite }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating type site:', error);
      throw error;
    }
  },

  updateFonctionnalites: async (siteId, fonctionnaliteIds) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/${siteId}/fonctionnalites`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fonctionnaliteIds }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating fonctionnalites:', error);
      throw error;
    }
  },

  getFonctionnalitesSuggestions: async (siteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/${siteId}/fonctionnalites-suggestions`, {
        headers: getAuthHeaders(),
      });
      const body = await handleResponse(response);
      return body.data;
    } catch (error) {
      console.error('Error fetching fonctionnalites suggestions:', error);
      throw error;
    }
  },

  getTypeSiteSuggestions: async (siteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/${siteId}/type-site-suggestions`, {
        headers: getAuthHeaders(),
      });
      const body = await handleResponse(response);
      return body.data;
    } catch (error) {
      console.error('Error fetching type site suggestions:', error);
      throw error;
    }
  },

  linkClient: async (siteId, dolibarrThirdpartyId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/${siteId}/client`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ dolibarrThirdpartyId }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error linking client to site:', error);
      throw error;
    }
  },

  linkCommercial: async (siteId, commercialId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/${siteId}/commercial`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ commercialId }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error linking commercial to site:', error);
      throw error;
    }
  },

  linkEntreprise: async (siteId, entrepriseLocaleId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/${siteId}/entreprise`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ entrepriseLocaleId }),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error linking entreprise to site:', error);
      throw error;
    }
  },

  autoLinkEntreprise: async (siteId, overwrite = false) => {
    try {
      const query = overwrite ? '?overwrite=1' : '';
      const response = await fetch(`${API_BASE_URL}/hebergement/${siteId}/auto-link-entreprise${query}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error auto-linking entreprise:', error);
      throw error;
    }
  },

  autoLinkAllEntreprises: async (overwrite = false) => {
    try {
      const query = overwrite ? '?overwrite=1' : '';
      const response = await fetch(`${API_BASE_URL}/hebergement/auto-link-entreprises${query}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error auto-linking all entreprises:', error);
      throw error;
    }
  },

  // Récupérer un site par URL
  getSiteByUrl: async (url) => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/by-url?url=${encodeURIComponent(url)}`, {
        headers: getAuthHeaders()
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching site by URL:', error);
      throw error;
    }
  },

  // Importer les données depuis MainWP
  importFromMainWP: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/import`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error importing from MainWP:', error);
      throw error;
    }
  },

  // Synchroniser avec MainWP
  syncWithMainWP: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/sync`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error syncing with MainWP:', error);
      throw error;
    }
  },

  // Tester la connexion MainWP
  testConnection: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/test-connection`, {
        headers: getAuthHeaders()
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error testing MainWP connection:', error);
      throw error;
    }
  },

  // Récupérer tous les sites (pour les statistiques)
  getAllSites: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/hebergement/list?limit=1000`, {
        headers: getAuthHeaders()
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching all sites:', error);
      throw error;
    }
  }
};

export default hebergementService;
